import { Flex, theme } from 'antd';
import { Bar, BarChart, ResponsiveContainer, Tooltip, type TooltipProps, XAxis } from 'recharts';
import { ChartTooltipWrapperEcho } from '@/components/charts/ChartTooltipEcho';
import type { AgePlaysResponse, SelectedPlatform } from '@/types/api/age';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { formatPercentage } from '@/utils/numberFormattingUtils';
import { themeRawValues } from '@/utils/styleUtils';
import { AgeDataByPlatformEcho } from './AgeDataByPlatformEcho';
import { TooltipContentEcho } from './TooltipContentEcho';

const chartColors = themeRawValues.colors.chartColors;

export const AgeChartEcho = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: AgePlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data>;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const { token } = theme.useToken();

  const handleTooltipHover = (event: { label?: string; percentage?: number }) => {
    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.chartTooltipHover, {
      widget: RevenueDashboardWidget.AgeDemographics,
      page: 'audience',
      platform: selectedPlatform,
      demographics: 'age',
      segment: event.label || '',
      percent: event.percentage ? `${Math.round(event.percentage)}%` : '',
    });
  };

  const filteredData =
    data.plays.find((play) => play.platform === selectedPlatform)?.age_distribution ?? [];

  return (
    <Flex
      vertical
      align="center"
      justify="space-between"
      style={{ width: '100%', maxWidth: 400, flex: 1, margin: '0 auto' }}
    >
      <ResponsiveContainer height={360} width="100%">
        <BarChart data={filteredData} margin={{ bottom: 32, top: 32 }}>
          <Tooltip
            cursor={false}
            isAnimationActive
            offset={32}
            allowEscapeViewBox={{ y: true }}
            animationDuration={1000}
            wrapperStyle={{ zIndex: token.zIndexPopupBase }}
            content={({ active, payload }: TooltipProps<number, string>) => (
              <ChartTooltipWrapperEcho>
                {active && payload?.[0]?.payload && (
                  <TooltipContentEcho
                    label={`${payload[0].payload.label} years old`}
                    value={formatPercentage(payload[0].payload.percentage)}
                    selectedPlatform={selectedPlatform}
                  />
                )}
              </ChartTooltipWrapperEcho>
            )}
          />
          <Bar
            dataKey="value"
            fill={chartColors.color3}
            onMouseEnter={handleTooltipHover}
            radius={[token.borderRadiusSM, token.borderRadiusSM, 0, 0]}
          />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{
              fill: token.colorText,
              fontSize: token.fontSize,
              fontWeight: 500,
            }}
            tickMargin={token.margin}
          />
        </BarChart>
      </ResponsiveContainer>
      <Flex justify="center" style={{ marginBlock: token.marginLG, maxWidth: '100%' }}>
        <AgeDataByPlatformEcho
          data={data}
          selectedPlatform={selectedPlatform}
          handlePlatformChange={handlePlatformChange}
        />
      </Flex>
    </Flex>
  );
};
